import { Trash, ChevronsUpDown } from "lucide-react";
import { useRef, useCallback, useEffect, memo, useMemo } from 'react';
import { usePlaceholder } from '../../../hooks/usePlaceholder';
import { useDispatch, useStore } from 'react-redux';
import { setLastEditedDocumentField } from '../../../store/reducers/index';
import { DocumentosModal, getDocumentos, findDocumento } from './DocumentosModal';
import { DocumentosTipoModal, getDocumentosTipos, findDocumentoTipo } from './DocumentosModal';
import { OptimizedInput } from '../../common/OptimizedInput';
import { useDebouncedUpdate } from '../../../hooks/useDebouncedUpdate';
import { updateDeclarationDocumentField } from '../../../utils/editableDataUpdates';
import { useIndexedFieldHandlerFactory } from '../../../utils/fieldHandlers';

const DeclarationDocumentsTableContentComponent = ({
  declarationIndex,
  isSpanish,
  declaration,
  editableData,
  setEditableData,
  setIsDirty,
  openDocModalIndex,
  setOpenDocModalIndex,
  openTipoModal,
  setOpenTipoModal,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const documentos = getDocumentos();
  const tipos = getDocumentosTipos();
  
  const editableDataRef = useRef(editableData);
  const latestEditableDataRef = useRef(editableData); // Track latest prop to merge with ref changes
  
  useEffect(() => {
    editableDataRef.current = editableData;
    latestEditableDataRef.current = editableData; // Keep latest prop in sync
  }, [editableData, declarationIndex]);

  // Debounced update: merge mutated ref changes with store data (preserves item/declaration changes)
  const handleUpdate = useCallback((updatedData, field, value, docIndex) => {
    const currentState = store.getState();
    const storeEditableData = currentState?.workflowSessions?.sessions?.[currentState?.workflowSessions?.activeSessionId]?.state?.editableData ||
                              currentState?.tabs?.tabs?.[currentState?.tabs?.activeTabId]?.editableData ||
                              currentState?.documents?.editableData;
    const normalizedStoreData = Array.isArray(storeEditableData) ? storeEditableData : 
                                (storeEditableData && typeof storeEditableData === 'object' ? [storeEditableData] : []);
    
    const refData = editableDataRef.current || updatedData;
    const refDocumentos = refData?.[declarationIndex]?.documentos; // Get mutated documentos from ref
    const baseData = (normalizedStoreData.length > 0 ? normalizedStoreData : null) || // Use store as base, merge documentos from ref 
                     editableData || 
                     latestEditableDataRef.current || 
                     updatedData;
    
    const latestData = baseData.map((decl, dIdx) => { // Merge: replace documentos from ref, keep rest from baseData
      if (dIdx === declarationIndex && refDocumentos) {
        return {
          ...decl,
          documentos: refDocumentos
        };
      }
      return decl;
    });
    const immutableCopy = latestData.map((decl, dIdx) => // Ensure specific field is set correctly 
      dIdx === declarationIndex ? {
        ...decl,
        documentos: decl.documentos.map((doc, idx) => 
          idx === docIndex ? { ...doc, [field]: value } : doc
        )
      } : decl
    );
    
    setEditableData(immutableCopy);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field,
      value,
      declarationIndex,
      partidaIndex: null,
      documentIndex: docIndex,
    }));
  }, [dispatch, setEditableData, setIsDirty, declarationIndex, store]);

  const { debouncedUpdate, cancelPendingUpdates } = useDebouncedUpdate(
    (updatedData, field, value, docIndex) => handleUpdate(updatedData, field, value, docIndex),
    300
  );

  const updateDocument = useCallback((docIndex, field, value) => { // Direct mutation during typing (O(1))
    const current = editableDataRef.current;
    const currentDoc = current?.[declarationIndex]?.documentos?.[docIndex];
    if (!currentDoc || currentDoc[field] === value) return;
    
    currentDoc[field] = value; // Mutate ref directly for performance
    
    if (typeof window !== 'undefined' && window.__validateFieldImmediately) {
      window.__validateFieldImmediately(declarationIndex, field, value, null, docIndex, null);
    }
    debouncedUpdate(current, field, value, docIndex); // Create immutable copy only after debounce
  }, [declarationIndex, debouncedUpdate]);

  const handleDelete = useCallback((docIndex) => {
    cancelPendingUpdates();
    const latestData = latestEditableDataRef.current || editableDataRef.current; // Use latest to preserve changes
    const updated = latestData.map((decl, dIdx) => 
      dIdx === declarationIndex ? {
        ...decl,
        documentos: decl.documentos.filter((_, idx) => idx !== docIndex)
      } : decl
    );
    
    editableDataRef.current = updated;
    latestEditableDataRef.current = updated;
    setEditableData(updated);
      setIsDirty(true);
      dispatch(setLastEditedDocumentField({
      field: '__declaration_documents__',
      value: null,
        declarationIndex,
      partidaIndex: null,
        documentIndex: docIndex,
      }));
  }, [declarationIndex, setEditableData, setIsDirty, dispatch, cancelPendingUpdates]);
    
  const getFieldHandler = useIndexedFieldHandlerFactory((docIndex, field, value) => {
    updateDocument(docIndex, field, value);
  });

  // Memoize all placeholder values to prevent unnecessary re-renders
  const enterNumberPlaceholder = usePlaceholder({ isSpanish, type: 'enter_number' });
  const enterDatePlaceholder = usePlaceholder({ isSpanish, type: 'enter_date' });

  const documentosToDisplay = useMemo(() => { // Read from editableData for immediate UI updates
    const currentDeclaration = editableData?.[declarationIndex];
    const documentos = currentDeclaration?.documentos || declaration?.documentos || [];
    return documentos;
  }, [
    editableData?.[declarationIndex]?.documentos, // Only depend on this declaration's documentos
    declaration?.documentos,
    declarationIndex
  ]);

  return (
    <div className="declaration-documents-table-content">
      <div className="overflow-auto">
        <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
              <th className="py-2 px-4 pt-2 pl-0"><p className="w-max">#</p></th>
              <th className="py-2 px-4 pt-2"><p className="w-max">{!isSpanish ? "Document Code" : "Código de Documento"}</p></th>
              <th className="py-2 px-4 pt-2"><p className="w-max">{!isSpanish ? "Type" : "Tipo"}</p></th>
              <th className="py-2 px-4 pt-2"><p className="w-max">{!isSpanish ? "Number" : "Número"}</p></th>
              <th className="py-2 px-4 pt-2"><p className="w-max">{!isSpanish ? "Date" : "Fecha"}</p></th>
            <th className="py-2 px-4 pt-2"></th>
          </tr>
        </thead>
        <tbody>
            {documentosToDisplay.map((doc, docIndex) => {
              const selectedDoc = doc.codigo_documento ? findDocumento(doc.codigo_documento) : null;
              const selectedTipo = doc.tipo ? findDocumentoTipo(doc.tipo) : null;
              const modalKey = `${declarationIndex}_declaration_${docIndex}`;
              const numeroHandler = getFieldHandler(docIndex, "numero");
              const fechaHandler = getFieldHandler(docIndex, "fecha");
              
              return (
            <tr key={docIndex}>
              <td className="py-4 px-4 pl-0 border-t">
                <span className="text-sm">{docIndex + 1}</span>
              </td>
              <td className="py-4 px-4 border-t">
                <div className="relative w-full">
                  <button
                    type="button"
                    className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${doc.codigo_documento ? '' : 'opacity-75'}`}
                    style={{ maxWidth: '260px' }}
                        onClick={() => setOpenDocModalIndex(modalKey)}
                    id={`declaration_documents_codigo_documento_${declarationIndex + 1}_${docIndex}`}
                  >
                        {selectedDoc ? (
                          <span className="truncate" style={{ maxWidth: '200px', display: 'inline-block' }} title={`${selectedDoc.codigo} - ${selectedDoc.nombre}`}>
                            {selectedDoc.codigo} {selectedDoc.nombre && <span className="text-xs text-gray-400 ml-2">({selectedDoc.nombre})</span>}
                          </span>
                        ) : doc.codigo_documento ? (
                          <span className="truncate" title={doc.codigo_documento}>{doc.codigo_documento}</span>
                    ) : (
                          <span className="truncate">{!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}</span>
                    )}
                    <ChevronsUpDown className="w-4 h-4" />
                  </button>
                  <DocumentosModal
                    documentos={documentos}
                        isOpen={openDocModalIndex === modalKey}
                    onClose={() => setOpenDocModalIndex(null)}
                        onSelect={(docSelected) => updateDocument(docIndex, 'codigo_documento', docSelected.codigo)}
                        selectedDocumento={selectedDoc}
                    title={!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}
                  />
                </div>
              </td>
              <td className="py-4 px-4 border-t">
                <button
                  type="button"
                  className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${doc.tipo ? '' : 'opacity-75'}`}
                      onClick={() => setOpenTipoModal({ ...openTipoModal, [modalKey]: true })}
                  id={`declaration_documents_tipo_${declarationIndex + 1}_${docIndex}`}
                >
                      {selectedTipo ? (
                        <span className="truncate">{selectedTipo.codigo} <span className="text-xs text-gray-400 ml-2">({selectedTipo.nombre})</span></span>
                      ) : doc.tipo ? (
                        <span className="truncate" title={doc.tipo}>{doc.tipo}</span>
                      ) : (
                        <span className="truncate">{!isSpanish ? 'Select Type' : 'Seleccionar Tipo'}</span>
                      )}
                  <ChevronsUpDown className="w-4 h-4" />
                </button>
                <DocumentosTipoModal
                      tipos={tipos}
                      isOpen={!!openTipoModal[modalKey]}
                      onClose={() => setOpenTipoModal({ ...openTipoModal, [modalKey]: false })}
                      onSelect={tipo => updateDocument(docIndex, 'tipo', Number(tipo.codigo))}
                      selectedTipo={selectedTipo}
                  title={!isSpanish ? 'Select Type' : 'Seleccionar Tipo'}
                />
              </td>
              <td className="py-4 px-4 border-t">
                    <OptimizedInput
                  id={`declaration_documents_numero_${declarationIndex + 1}_${docIndex}`}
                  value={doc.numero}
                      placeholder={enterNumberPlaceholder}
                  onChange={numeroHandler}
                  className="w-full border rounded px-2 py-1 pr-8"
                />
              </td>
              <td className="py-4 px-4 border-t">
                    <OptimizedInput
                  id={`declaration_documents_fecha_${declarationIndex + 1}_${docIndex}`}
                  value={doc.fecha}
                      placeholder={enterDatePlaceholder}
                  onChange={fechaHandler}
                  className="w-full border rounded px-2 py-1 pr-8"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <button
                  className="py-1 text-xs flex items-center gap-1 text-red-500"
                      onClick={() => handleDelete(docIndex)}
                >
                      <Trash className="size-4 stroke-red-500" />
                  {isSpanish ? "Eliminar" : "Delete"}
                </button>
              </td>
            </tr>
              );
            })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const areDeclarationDocumentsTableContentPropsEqual = (prevProps, nextProps) => { // Memo: only re-render if relevant props change
  if (prevProps.declarationIndex !== nextProps.declarationIndex) return false;
  if (prevProps.isSpanish !== nextProps.isSpanish) return false;
  if (prevProps.openDocModalIndex !== nextProps.openDocModalIndex) return false;
  if (prevProps.setOpenDocModalIndex !== nextProps.setOpenDocModalIndex) return false;
  if (prevProps.setOpenTipoModal !== nextProps.setOpenTipoModal) return false;
  
  const prevDocumentos = prevProps.editableData?.[prevProps.declarationIndex]?.documentos;
  const nextDocumentos = nextProps.editableData?.[nextProps.declarationIndex]?.documentos;
  if (prevDocumentos !== nextDocumentos) return false;
  
  const prevDeclDocumentos = prevProps.declaration?.documentos;
  const nextDeclDocumentos = nextProps.declaration?.documentos;
  if (prevDeclDocumentos !== nextDeclDocumentos) return false;
  
  return true;
};

export const DeclarationDocumentsTableContent = memo(DeclarationDocumentsTableContentComponent, areDeclarationDocumentsTableContentPropsEqual);
